import { ActionType } from 'typesafe-actions';
import { all, call, put, takeEvery } from 'redux-saga/effects';

import {
  INTERNAL_SERVER_ERROR,
  REQUIREMENTS_NPA_EXIST,
  REQUIREMENTS_NPA_INVALID_FORM,
  REQUIREMENTS_STRUCTURAL_UNIT_COMPONENT_DUPLICATE,
  REQUIREMENTS_STRUCTURAL_UNIT_DUPLICATE,
} from 'CorePackage/MainModule/constants/notices';
import { IRequirementNpaSuAndMandatoryResponse } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementNpaSuAndMandatoryResponse';

import {
  addSelectedMandatoryAction,
  addToStructuralUnitList,
  createMandatoryListAction,
  createNpaAction,
  createStructuralUnitsAction,
  createSusAction,
  createSusWithMandatoryAction,
  editNpaAction,
  getCurrentNpaAction,
  getMandatoryListAction,
  getNpaCountAction,
  getNpaListAction,
  getNpaListNewAction,
  getStructuralUnitListAction,
  removeAllNoticesAction,
  requirementsNpaFindSelectOptionsByQuery,
  setCreatedSelectedStructuralUnits,
  setCurrentNpaAction,
  setIdMap,
  setIsListStructuralUnitLoaded,
  setIsMandatoryCreatedAction,
  setIsNpaCreatedAction,
  setIsNpaEditedAction,
  setIsNpaEditedWithErrorAction,
  setIsStructuralUnitCreatedAction,
  setIsSuCreatedAction,
  setMandatoryListAction,
  setNpaCountAction,
  setNpaListAction,
  setNpaListNew,
  setSavedNpaAction,
  setStructuralUnitListAction,
} from '../actions/requirementsNpaActions';
import { RequirementsNpaActionsEnum } from '../enums/RequirementsNpaActionsEnum';
import { savedTakeEvery } from '../../../CorePackage/MainModule/common/savedTakeEvery';
import { coreSetLoading } from '../../../CorePackage/MainModule/actions/coreActions';
import { IRequirementNpaCount } from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementNpaCount';
import {
  createMandatory,
  createNpa,
  createStructuralUnitsForNpa,
  deleteNpa,
  editNpa,
  fetchMandatoryList,
  fetchNpaCount,
  fetchNpaList,
  fetchNpaListNew,
  fetchRequirementsNpaByQuery,
  fetchStructuralUnitList,
  fetchStructuralUnitsRequirements,
  fetchActiveDuplicateNpa,
} from '../../../APIPackage/RequirementsNpaModule/services/requirementsNpaApi';
import { IRequirementNpa } from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import { IRequirementStructuralUnit } from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementStructuralUnit';
import {
  IRequirementsMandatory,
  IRequirementsMandatoryRaw,
} from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementsMandatory';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { IResponseError } from '../../../APIPackage/MainModule/interfaces/IResponseError';
import { IEditNpaResponse } from '../../../APIPackage/RequirementsNpaModule/interfaces/IEditNpaResponse';
import { IRequirementNpaResponse } from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementNpaResponse';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { ISelectOption } from '../../../UIPackage/MainModule/components/SelectInput';

export function* watchRequirementsNpaSaga() {
  yield savedTakeEvery(RequirementsNpaActionsEnum.GET_NPA_COUNT, getNpaCountSaga);
  yield savedTakeEvery(RequirementsNpaActionsEnum.GET_NPA_LIST, getNpaListSaga);
  yield savedTakeEvery(RequirementsNpaActionsEnum.GET_STRUCTURAL_UNIT_LIST, getStructuralUnitListSaga);
  yield savedTakeEvery(RequirementsNpaActionsEnum.GET_MANDATORY_LIST, getMandatoryListSaga);
  yield savedTakeEvery(RequirementsNpaActionsEnum.CREATE_NPA, createNpaSaga);
  yield savedTakeEvery(RequirementsNpaActionsEnum.CREATE_SUS, createSuSaga);
  yield savedTakeEvery(RequirementsNpaActionsEnum.CREATE_SUS_WITH_MANDATORY, createSuWithMandatorySaga);
  yield savedTakeEvery(RequirementsNpaActionsEnum.EDIT_NPA, editNpaSage);
  yield savedTakeEvery(RequirementsNpaActionsEnum.CREATE_STRUCTURAL_UNITS, createStructuralUnitsSaga);
  yield savedTakeEvery(RequirementsNpaActionsEnum.CREATE_MANDATORY_LIST, createMandatoryListSaga);
  yield savedTakeEvery(RequirementsNpaActionsEnum.SHOW_VALIDATION_NOTICE, showValidationNotifySaga);
  yield savedTakeEvery(RequirementsNpaActionsEnum.GET_NPA_LIST_NEW, getNpaListNewSaga);
  yield savedTakeEvery(RequirementsNpaActionsEnum.GET_CURRENT_NPA, getActiveDuplicateNpaSaga);
  yield takeEvery(
    RequirementsNpaActionsEnum.REQUIREMENTS_NPA_FIND_SELECT_OPTIONS_BY_QUERY,
    requirementsNpaFindSelectOptionsByQuerySaga,
  );
}

export function* getNpaCountSaga(action: ActionType<typeof getNpaCountAction>) {
  const { filter } = action.payload;

  try {
    yield put(coreSetLoading(true));
    const data: IRequirementNpaCount = yield fetchNpaCount(filter);

    yield put(setNpaCountAction(data.count));
  } finally {
    yield put(coreSetLoading(false));
  }
}

export function* getNpaListSaga(action: ActionType<typeof getNpaListAction>) {
  const { filter } = action.payload;

  try {
    yield put(coreSetLoading(true));
    const data: IRequirementNpa[] = yield fetchNpaList(filter);

    yield put(setNpaListAction(data));
  } catch {
    yield put(setNpaListAction([]));
    yield put(
      addNoticeAction({
        renderContent: INTERNAL_SERVER_ERROR,
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.REQUIREMENTS_NPA,
      }),
    );
  } finally {
    yield put(coreSetLoading(false));
  }
}

export function* getStructuralUnitListSaga(action: ActionType<typeof getStructuralUnitListAction>) {
  const { npaId, oivControlId, oivControlTypeId } = action.payload;

  try {
    yield put(coreSetLoading(true));
    const data: IRequirementStructuralUnit[] = yield fetchStructuralUnitList(npaId, oivControlId, oivControlTypeId);

    yield put(setStructuralUnitListAction(data));
    yield put(setIsListStructuralUnitLoaded(true));
  } finally {
    yield put(coreSetLoading(false));
  }
}

export function* getMandatoryListSaga(action: ActionType<typeof getMandatoryListAction>) {
  const { npaId, suId, filter } = action.payload;

  try {
    yield put(coreSetLoading(true));
    const data: IRequirementsMandatory[] = yield fetchMandatoryList(npaId, suId, filter);

    yield put(setMandatoryListAction(suId, data));
  } finally {
    yield put(coreSetLoading(false));
  }
}

export function* createNpaSaga(action: ActionType<typeof createNpaAction>) {
  const { npa, structuralUnits } = action.payload;
  let data: IRequirementNpa | null = null;

  try {
    yield put(coreSetLoading(true));
    data = yield createNpa(npa);
    if (data && structuralUnits && structuralUnits.length > 0) {
      yield createStructuralUnitsForNpa(data.id, structuralUnits);
    }
    yield put(setSavedNpaAction(data));
    yield put(setIsNpaCreatedAction(true));
  } catch (err) {
    const error: IResponseError = err as IResponseError;

    if (error.status === 409) {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_NPA_EXIST,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else if (error.clientErrorCode === 'E1052') {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_STRUCTURAL_UNIT_COMPONENT_DUPLICATE,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else if (error.clientErrorCode === 'E1053') {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_STRUCTURAL_UNIT_DUPLICATE,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    }
    if (data) yield deleteNpa(data.id);
  } finally {
    yield put(coreSetLoading(false));
  }
}

export function* createSuSaga(action: ActionType<typeof createSusAction>) {
  const { npaId, structuralUnits, callback } = action.payload;

  try {
    yield put(coreSetLoading(true));
    let data: IRequirementStructuralUnit[] = [];

    if (structuralUnits && structuralUnits.length > 0) {
      data = yield createStructuralUnitsForNpa(npaId, structuralUnits);
    }

    let suIdNew: number = -1;

    if (data.length) suIdNew = data[0].id;
    callback(suIdNew);
    yield put(setIsSuCreatedAction(true));
  } catch (err) {
    const error: IResponseError = err as IResponseError;

    if (error.status === 409) {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_NPA_EXIST,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else if (error.clientErrorCode === 'E1052') {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_STRUCTURAL_UNIT_COMPONENT_DUPLICATE,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else if (error.clientErrorCode === 'E1053') {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_STRUCTURAL_UNIT_DUPLICATE,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    }
  } finally {
    yield put(coreSetLoading(false));
  }
}

export function* createSuWithMandatorySaga(action: ActionType<typeof createSusWithMandatoryAction>) {
  const {
    npaId,
    knoVersionId,
    supervisionVersionId,
    objectSubkindIds,
    riskIndicatorIds,
    objectTypeIds,
    objectKindIds,
    units,
    callback,
  } = action.payload;

  try {
    yield put(coreSetLoading(true));
    const data: IRequirementNpaSuAndMandatoryResponse = yield fetchStructuralUnitsRequirements({
      npaId,
      knoVersionId,
      supervisionVersionId,
      objectSubkindIds,
      riskIndicatorIds,
      objectTypeIds,
      objectKindIds,
      units,
    });

    yield put(setIsSuCreatedAction(true));

    let mandatoryId = -1;
    let suId = -1;

    if (Object.keys(data).length) {
      const key = Object.keys(data)[0];

      suId = parseInt(key, 10);
      if (data[key].length) {
        mandatoryId = data[key][0].id;
      }
    }
    callback(suId, mandatoryId);
  } catch (err) {
    const error: IResponseError = err as IResponseError;

    if (error.status === 409) {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_NPA_EXIST,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else if (error.clientErrorCode === 'E1052') {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_STRUCTURAL_UNIT_COMPONENT_DUPLICATE,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else if (error.clientErrorCode === 'E1053') {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_STRUCTURAL_UNIT_DUPLICATE,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    }
  } finally {
    yield put(coreSetLoading(false));
  }
}

export function* editNpaSage(action: ActionType<typeof editNpaAction>) {
  const { npa, structuralUnits } = action.payload;
  let data: IEditNpaResponse | null = null;

  try {
    yield put(coreSetLoading(true));
    data = yield editNpa(npa);

    yield put(setSavedNpaAction(data));
    yield put(setIdMap(data));
    if (data && structuralUnits && structuralUnits.length > 0) {
      yield createStructuralUnitsForNpa(data.id, structuralUnits);
    }
    yield put(setIsNpaEditedAction(true));
  } catch (err) {
    if (data) {
      yield put(setIsNpaEditedWithErrorAction(true));
    }
    const error: IResponseError = err as IResponseError;

    if (error.status === 409) {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_NPA_EXIST,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else if (error.clientErrorCode === 'E1052') {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_STRUCTURAL_UNIT_COMPONENT_DUPLICATE,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else if (error.clientErrorCode === 'E1053') {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_STRUCTURAL_UNIT_DUPLICATE,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    }
  } finally {
    yield put(coreSetLoading(false));
  }
}

export function* createStructuralUnitsSaga(action: ActionType<typeof createStructuralUnitsAction>) {
  const { units, npaId } = action.payload;
  const selectedUnits = units?.filter(value => value.isSelected);
  const otherUnits = units?.filter(value => !value.isSelected);

  try {
    yield put(coreSetLoading(true));
    if (otherUnits?.length) {
      const data: IRequirementStructuralUnit[] = yield createStructuralUnitsForNpa(npaId, otherUnits);

      yield put(addToStructuralUnitList(data));
    }

    if (selectedUnits?.length) {
      const data: IRequirementStructuralUnit[] = yield createStructuralUnitsForNpa(npaId, selectedUnits);

      yield put(addToStructuralUnitList(data));
      yield put(setCreatedSelectedStructuralUnits(data));
    } else yield put(setCreatedSelectedStructuralUnits([]));

    yield put(setIsStructuralUnitCreatedAction(true));
  } catch (err) {
    const error: IResponseError = err as IResponseError;

    if (error.clientErrorCode === 'E1052') {
      yield put(
        addNoticeAction({
          renderContent: REQUIREMENTS_STRUCTURAL_UNIT_COMPONENT_DUPLICATE,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    } else {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.REQUIREMENTS_NPA,
        }),
      );
    }
  } finally {
    yield put(coreSetLoading(false));
  }
}

export function* createMandatoryListSaga(action: ActionType<typeof createMandatoryListAction>) {
  const { mandatoryList, npaId, callback } = action.payload;

  try {
    yield put(coreSetLoading(true));

    let mandatoryId: number = -1;

    if (mandatoryList) {
      yield all(
        mandatoryList.map(value =>
          call(createMandatorySaga, value, npaId, id => {
            mandatoryId = id;
          }),
        ),
      );
    }
    yield put(setIsMandatoryCreatedAction(true));
    if (callback) callback(mandatoryId);
  } finally {
    yield put(coreSetLoading(false));
  }
}

function* createMandatorySaga(
  mandatory: IRequirementsMandatoryRaw,
  npaId: number,
  setMandatoryId: (id: number) => void,
) {
  try {
    const suId = mandatory.structuralUnit?.id;

    if (typeof suId !== 'number') return;
    const data: IRequirementsMandatory = yield createMandatory({ mandatory, npaId, suId });

    setMandatoryId(data.id);

    if (mandatory.isSelected) yield put(addSelectedMandatoryAction(data));
  } catch (e) {
    yield put(
      addNoticeAction({
        renderContent: INTERNAL_SERVER_ERROR,
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.REQUIREMENTS_NPA,
      }),
    );
  }
}

export function* showValidationNotifySaga() {
  yield put(removeAllNoticesAction());
  yield put(
    addNoticeAction({
      renderContent: REQUIREMENTS_NPA_INVALID_FORM,
      noticeType: NoticeTypesEnum.ERROR,
      messageType: MessageTypesEnum.REQUIREMENTS_NPA,
    }),
  );
}

export function* getNpaListNewSaga(action: ActionType<typeof getNpaListNewAction>) {
  const { knoVersionId, supervisionVersionId, npaIds, mrIds } = action.payload;

  try {
    yield put(coreSetLoading(true));
    const data: IRequirementNpaResponse = yield fetchNpaListNew({ knoVersionId, supervisionVersionId, npaIds, mrIds });

    yield put(setNpaListNew(data));
  } finally {
    yield put(coreSetLoading(false));
  }
}

export function* getActiveDuplicateNpaSaga(action: ActionType<typeof getCurrentNpaAction>) {
  const { npaId, knoVersionId, supervisionVersionId } = action.payload;

  try {
    yield put(coreSetLoading(true));
    const data: IRequirementNpaResponse = yield fetchActiveDuplicateNpa(npaId, knoVersionId, supervisionVersionId);

    yield put(setCurrentNpaAction(data));
  } finally {
    yield put(coreSetLoading(false));
  }
}

export function* requirementsNpaFindSelectOptionsByQuerySaga(
  action: ActionType<typeof requirementsNpaFindSelectOptionsByQuery>,
) {
  const { query, callback } = action.payload;

  const data: IRequirementNpa[] = yield fetchRequirementsNpaByQuery(query);
  const selectOptions: ISelectOption[] = data.map(item => ({
    id: item.id,
    value: item,
    label: `№ ${item.number} от ${item.acceptanceDate} - ${item.title}`,
  }));

  callback(selectOptions);
}
